import { useState } from "react";
import { Building2, User } from "lucide-react";

export interface AvatarProps {
  /** External avatar URL (e.g. Baby Chops `Profile Image URL`). Used as-is
   * for `<img src>` — never proxied/cached by this primitive. */
  src?: string | null;
  /** Contact's display name — feeds the alt text and the monogram fallback
   * initials. Omit/empty falls back to a generic person-silhouette icon. */
  name?: string | null;
  /** Monogram override. When set, used instead of the initials derived from
   *  `name` (which stays the accessible label). For a caller that knows the
   *  name better than a whitespace split does — e.g. the person panel skips
   *  tokens with no letter, so "☆ Mark Hoekstra" reads "MH", not "☆H". */
  initials?: string | null;
  /** Monotonic t-shirt scale: sm=1.5rem, md=2rem, lg=3.5rem, xl=5rem, 2xl=8rem. */
  size?: keyof typeof SIZES;
  /** `circle` (default) for a person; `square` (8px radius, a building as the
   *  no-name fallback) for a company -- the company lists epic, 2026-09-25. */
  shape?: "circle" | "square";
  className?: string;
}

/** Per-size classes: circle dims, monogram typography, fallback icon dims. */
const SIZES = {
  sm: { dims: "h-6 w-6", text: "text-caption", icon: "h-3.5 w-3.5" },
  md: { dims: "h-8 w-8", text: "text-caption", icon: "h-4 w-4" },
  lg: { dims: "h-14 w-14", text: "text-h3", icon: "h-6 w-6" },
  xl: { dims: "h-20 w-20", text: "text-h2", icon: "h-8 w-8" },
  "2xl": { dims: "h-32 w-32", text: "text-h1", icon: "h-12 w-12" },
} as const;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  const first = parts[0] ?? "";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? "";
  return `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase();
}

/**
 * Avatar — a small circular contact image with a graceful fallback.
 *
 * Renders `<img src={src}>` when `src` is present (Baby Chops CSV imports
 * carry a "Profile Image URL" column — stored as an external URL string,
 * display-only, never sent to Thrive) AND that image has not already failed
 * to load. When `src` is absent, OR the image fails to load (404, expired
 * LinkedIn CDN link, hotlink-blocked, network error — all common for
 * externally-hosted URLs we don't control), falls back to a monogram circle
 * built from `name`'s initials, or — when `name` is also absent/empty — a
 * generic `lucide-react` person-silhouette icon. The load-failure is
 * tracked by URL (not a plain boolean) so a row recycled for a different
 * contact (e.g. list virtualization / re-sort) automatically re-attempts
 * the new `src` instead of an old failure sticking across re-renders.
 *
 * Tokens only: `bg-surface`/`text-text-muted`/`border-border`, `rounded-full`
 * — no inline `style={{}}`, no arbitrary `text-[Npx]`.
 */
export function Avatar({
  src,
  name,
  initials: initialsOverride,
  size = "md",
  shape = "circle",
  className,
}: AvatarProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const imageFailed = src != null && src === failedSrc;

  const base = `inline-flex shrink-0 items-center justify-center overflow-hidden ${shape === "square" ? "rounded-md" : "rounded-full"} border border-border bg-surface ${SIZES[size].dims}`;
  const classes = [base, className].filter(Boolean).join(" ");
  const label = name?.trim() || (shape === "square" ? "Company" : "Contact");
  const Fallback = shape === "square" ? Building2 : User;

  if (src && !imageFailed) {
    return (
      <img
        data-testid="avatar-image"
        src={src}
        alt={label}
        // `object-cover` + `aspect-square`: a source photo of any ratio fills
        // the fixed circle instead of stretching into an oval (2026-10-07).
        className={`${classes} aspect-square object-cover`}
        onError={() => setFailedSrc(src)}
      />
    );
  }

  const glyph = initialsOverride?.trim() || (name ? initials(name) : "");
  if (glyph) {
    return (
      <span
        data-testid="avatar-initials"
        aria-label={label}
        className={`${classes} ${SIZES[size].text} font-bold text-text-secondary`}
      >
        {glyph}
      </span>
    );
  }

  return (
    <span
      data-testid="avatar-placeholder"
      aria-label={label}
      className={`${classes} text-text-muted`}
    >
      <Fallback aria-hidden="true" className={SIZES[size].icon} />
    </span>
  );
}
